import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useApp, setPage, Loading } from '@laboratoria/sdk-react';
import colors from '@laboratoria/sdk-react/src/theme/colors';
import { registerMessages } from '../../intl';
import es from './intl/es';
import pt from './intl/pt';
import Footer from '../Footer';
import Program from './Program';
import { fetchOpenCohorts } from '../../utils/cohorts';

registerMessages('home', { es, pt });

const Home = ({ lang }) => {
  const app = useApp();
  const [programs, setPrograms] = useState();
  const [cohorts, setCohorts] = useState();
  const [countriesByCode, setCountriesByCode] = useState();
  const intl = useIntl();

  useEffect(() => {
    setPage({
      title: intl.formatMessage({ id: 'home/page-title' }),
      description: intl.formatMessage({ id: 'home/page-description' }),
    });
  }, [intl]);

  useEffect(() => {
    fetchOpenCohorts(app, lang)
      .then(setCohorts)
      .catch(setCohorts);
  }, [app, lang]);

  useEffect(() => {
    if (!cohorts || cohorts instanceof Error) {
      return;
    }
    const cohortsByProgramId = cohorts.reduce(
      (memo, cohort) => ({
        ...memo,
        [cohort.programId]: (memo[cohort.programId] || []).concat(cohort),
      }),
      {},
    );
    app.program.findMany({
      where: {
        hidden: false,
        langs: {
          has: lang,
        },
      },
      include: {
        track: true,
      },
    })
      .then(programs => programs.map(program => ({
        ...program,
        cohorts: cohortsByProgramId[program.id] || [],
      })))
      .then(setPrograms)
      .catch(setPrograms);
  }, [app, lang, cohorts]);

  useEffect(() => {
    if (!cohorts || cohorts instanceof Error) {
      return;
    }

    const countryCodes = cohorts.reduce(
      (memo, { admissionCountries }) => admissionCountries.reduce(
        (prev, countryCode) => (
          prev.includes(countryCode)
            ? prev
            : prev.concat(countryCode)
        ),
        memo,
      ),
      [],
    );

    app.country.findMany({
      where: {
        code: {
          in: countryCodes,
        },
      },
    })
      .then(countries => countries.reduce(
        (memo, country) => ({ ...memo, [country.code]: country }),
        {},
      ))
      .then(setCountriesByCode)
      .catch(setCountriesByCode);
  }, [app, cohorts]);

  if (cohorts instanceof Error) {
    return <Alert severity="error">Error cargando fechas disponibles</Alert>
  }

  if (programs instanceof Error) {
    return <Alert severity="error">Error cargando programas</Alert>;
  }

  if (countriesByCode instanceof Error) {
    return <Alert severity="error">Error cargando países</Alert>;
  }

  if (!programs || !countriesByCode) {
    return <Loading />;
  }

  return (
    <>
      <Container
        maxWidth="lg"
        disableGutters={false}
        sx={{
          marginBottom: { xs: 8, sm: 10, },
        }}>
        <Typography
          variant="h1"
          sx={{
            backgroundColor: colors.yellow.main,
            marginBottom: {
              xs: 3,
              sm: 6,
            },
            paddingLeft: {
              xs: 4,
            },
            paddingRight: {
              xs: 6,
              sm: 8,
              md: 18,
            },
            paddingY: {
              xs: 3,
              sm: 7,
            },
            borderRadius: '0.938rem',
            textAlign: 'left',
            fontWeight: '600',
            fontSize: {
              xs: '1.625rem',
              sm: '2.688rem',
              md: '3.125rem',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: '0.875rem',
                sm: '1.063rem',
                md: '1.25rem'
              },
              fontWeight: '600',
              textTransform: 'uppercase',
              backgroundColor: '#F2F2F2',
              maxWidth: {
                xs: '181px',
                sm: '226px',
                md: '263px'
              },
              maxHeight: '42px',
              textAlign: 'center',
              paddingY: {
                xs: 1 / 8,
                md: 1 / 2
              },
              marginTop: {
                xs: 4,
                sm: 6,
              },
              marginBottom: {
                xs: 2,
              },
            }}
          >
            <FormattedMessage id="home/callout-text" />
          </Typography>
          <FormattedMessage id="home/title" />
        </Typography>
        <Grid
          container
          sx={{
            paddingX: { md: 2 },
            padding: { xs: '0px', md: '0px 30px' },
          }}
        >
          {programs.map(program => (

            <Grid

              key={program.id}
              item
              {...(
                programs.length >= 3
                  ? {
                    sm: 6,
                    md: 4,
                    sx: { paddingRight: { sm: 2, md: 0, } },
                  }
                  : programs.length === 2
                    ? {
                      sm: 6,
                      sx: { paddingRight: { sm: 2, md: 0, } },
                    }
                    : {
                      sm: 12,
                      md: 12,
                      sx: { paddingRight: { sm: 0, md: 0, } },
                    }
              )}
            >
              <Program
                lang={lang}
                program={program}
                countriesByCode={countriesByCode}
                programCount={programs.length}
              />
            </Grid>

          ))}
        </Grid>
      </Container>
      <Container>
        <Footer lang={lang} />
      </Container>
    </>
  );
};

export default Home;
